import React from 'react';

import { useTranslation } from 'react-i18next';
import { AutoTable, InfoText, Icon } from '@weezevent/nacre';
import { ListSelect } from '../inputs/inputs';
import { useDebounce } from '../widget-selector/widget-selector';

import Style from './selector.module.css';

/**
 * Default Tabs of objects selected.
 * This function can be passed on objectSelector params to overrid it and make your own tab
 * @param {*} param0
 */
export function SelectedItems({ objects = [], onRemoveObject = () => {}, name = null }) {
    const { t } = useTranslation();
    const label_name = name || t('ticket.widget.singleEvent.selectedObjectName');

    if (!objects.length) {
        return null;
    }

    return (
        <AutoTable
            pk="id"
            useNew
            columns={[
                {
                    key: 'obj_name',
                    label: label_name,
                    render: obj => {
                        return <InfoText subtitle={obj.id}>{obj.name}</InfoText>;
                    }
                },
                {
                    key: 'delete',
                    render: obj => {
                        return <Icon name="cross" onClick={() => onRemoveObject(obj)} />;
                    }
                }
            ]}
            objects={[...objects]}
        />
    );
}

/**
 * Object Selector.
 * Display an object selector with a List Select form nacre.
 * If many is true, a tab with all selected object is displayed to easily manage objects.
 *
 * @param objects - list of objects already selected. Can be only one object
 * @param Model - Model to retrieve all objects
 * @param requestParams - request param for useApiModel
 * @param requestDeps - dependancies for useApiModel
 * @param label - label of field
 * @param placeholder - placeholder of field
 * @param onchange - function to perform on actions
 * @param many - boolean to know if we have to select many events or not
 * @param sort_function - Function to sort objects. Default : alphabeticaly by name of object
 * @param map_function - Mapping function for objects using to create options.
 */
export function ObjectSelector({
    objects,
    Model,
    requestParams,
    queryParams = {},
    requestDeps = [],
    label = '',
    placeholder = '',
    onChange,
    many = false,
    sort_function,
    map_function,
    children,
    error = null,
    disabled = false,
    className = null
}) {
    const default_sort_function = React.useMemo(() => {
        return (
            sort_function ||
            ((a, b) => {
                return a.name < b.name ? -1 : a.name === b.name ? 0 : 1;
            })
        );
    }, [sort_function]);

    const default_map_function = React.useMemo(() => {
        return (
            map_function ||
            (obj => ({
                text: obj.name,
                label: obj.name,
                value: obj.id,
                object: obj
            }))
        );
    }, [map_function]);

    const [objectsModel, loadingObjects, syncObjects] = Model.useApiModel({ ...requestParams, ...queryParams }, requestDeps);

    const objects_tab = React.useMemo(() => {
        if (objects instanceof Array) {
            return objects;
        }
        // events is not an array
        if (!objects) {
            return [];
        }
        // not null
        return [objects];
    }, [objects]);

    const options = React.useMemo(() => {
        // Sort objects
        const ordered_objects = objectsModel?.sort(default_sort_function) || [];
        const list_pk_selected = objects_tab.map(obj => obj.pk);

        // we have to do this crappy following code because on
        //   - many = false case, we want to display the name selected into input, and option has to be in options
        //   - many = true case, we want to delete choice selected in options
        if (many) {
            return ordered_objects?.filter(obj => !list_pk_selected.includes(obj.pk)).map(default_map_function);
        }
        return ordered_objects?.map(default_map_function);
    }, [objectsModel, objects_tab, default_sort_function, default_map_function, many]);

    const _search = React.useCallback(
        value => {
            syncObjects({
                query: {
                    ...queryParams.query,
                    search: value
                }
            });
        },
        [syncObjects, queryParams]
    );

    const search = useDebounce(_search);

    return (
        <div className={`${Style['widget-selector-container']} ${className}`}>
            <ListSelect
                search
                useNew
                value={many === false && objects ? objects.pk : null}
                loading={loadingObjects}
                label={label}
                closeOnDocumentClick={true}
                closeOnListClick={many === true ? false : true}
                closeOnTriggerClick={true}
                placeholder={placeholder}
                options={options}
                onChange={(value, { object }) => {
                    // Add to list of events
                    if (many === false) {
                        onChange(object);
                    } else {
                        onChange([...objects, object]);
                    }
                    return;
                }}
                onInputChange={({ target: { value } }) => search(value)}
                error={error}
                disabled={disabled}
            >
                {children}
            </ListSelect>
        </div>
    );
}
